import React, { FC, useState, useEffect } from 'react';
import { SCUIContent } from '../../../atomic/organisms';
import './EditRoles.scss';
import { SCUIButton } from '../../../atomic/atoms';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import { OperatorQuery } from '../../../services/query';
import { ER } from './editRoles.classes';

import {
    Paper,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
    Box,
    Chip,
    Autocomplete,
    CircularProgress,
} from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const privileges = ['Select', 'Update', 'Insert', 'Delete', 'Print', 'PUT'];

interface User {
    userRef: string;
    userName: string;
    fullName?: string;
}

export const EditRoles: FC<any> = () => {
    const { t } = useTranslation();
    const [rules, setRulesName] = React.useState(['']);
    const [roleManagers, setRoleManagers] = useState<User[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    const handleChange = (event: any) => {
        const {
            target: { value },
        } = event;
        setRulesName(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value
        );
    };

    const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const response = await OperatorQuery.GetOperatorList();
            if (response && response.model && response.model.data) {
                const users = response.model.data.map((user: any) => ({
                    userRef: user.userRef || user.applicationUserId,
                    userName: user.userName,
                    fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                }));
                setAllUsers(users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleRoleManagersChange = (event: any, newValue: User[]) => {
        setRoleManagers(newValue);
    };

    const handleRoleManagerDelete = (userToDelete: User) => {
        setRoleManagers(roleManagers.filter((user) => user.userRef !== userToDelete.userRef));
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleSave = () => {
        // Prepare data for API call
        const roleData = {
            rules: rules,
            roleManagers: roleManagers.map((user) => user.userRef), // Send userRef array
        };

        console.log('Role data to save:', roleData);

        // Here you would call your API to save the role with managers
        // Example: RoleCommand.UpdateRole(roleData)
        //   .then(() => {
        //     Swal.fire('Success', 'Role updated successfully', 'success');
        //   })
        //   .catch((error) => {
        //     Swal.fire('Error', 'Failed to update role', 'error');
        //   });
    };

    console.info(rules);

    return (
        <SCUIContent headerName={'Kural ve Yetkileri Düzenle '} className={ER.page}>
            <Grid container direction="row" className={ER.root}>
                <Grid
                    size={{
                        lg: 8,
                        md: 12,
                        xs: 12,
                    }}
                >
                    <Paper className={ER.formPaper} sx={{ padding: '2rem', margin: '2rem', width: '100%' }}>
                        <Grid container spacing={3} className={ER.formGrid}>
                            <Grid
                                className={ER.privilegesBox}
                                size={{
                                    md: 12,
                                    xs: 12,
                                }}
                            >
                                <FormControl className={ER.privilegesControl} sx={{ m: 1, width: 300 }}>
                                    <InputLabel id="demo-multiple-checkbox-label">Banner Görüntüleme</InputLabel>
                                    <Select
                                        className={ER.inputPrivileges}
                                        labelId="demo-multiple-checkbox-label"
                                        id="demo-multiple-checkbox"
                                        multiple
                                        value={rules}
                                        onChange={handleChange}
                                        input={<OutlinedInput label="Banner Görüntüleme" />}
                                        renderValue={(selected) => selected.join(',')}
                                        MenuProps={MenuProps}
                                    >
                                        {privileges.map((privilege) => (
                                            <MenuItem
                                                key={privilege}
                                                value={privilege}
                                                className={ER.privilegeMenuItem}
                                                data-privilege={privilege}
                                                data-checked={String(rules.indexOf(privilege) > -1)}
                                            >
                                                <Checkbox
                                                    className={ER.privilegeMenuItemCheckbox}
                                                    data-privilege={privilege}
                                                    checked={rules.indexOf(privilege) > -1}
                                                />
                                                <ListItemText
                                                    className={ER.privilegeMenuItemText}
                                                    primary={privilege}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Role Managers Section */}
                            <Grid
                                className={ER.managersBox}
                                size={{
                                    md: 12,
                                    xs: 12,
                                }}
                            >
                                <Box className={ER.managersInner} sx={{ m: 1 }}>
                                    <Typography
                                        className={ER.managersTitle}
                                        variant="h6"
                                        sx={{ mb: 2, fontWeight: 600 }}
                                    >
                                        {t('roleManagement.roleManagers')}
                                    </Typography>
                                    <Typography
                                        className={ER.managersDescription}
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 2 }}
                                    >
                                        {t('roleManagement.roleManagersDescription')}
                                    </Typography>

                                    {/* User Search Autocomplete */}
                                    <Autocomplete
                                        className={ER.inputManagersAutocomplete}
                                        multiple
                                        id="role-managers-autocomplete"
                                        options={allUsers}
                                        value={roleManagers}
                                        onChange={handleRoleManagersChange}
                                        loading={isLoadingUsers}
                                        getOptionLabel={(option) =>
                                            option.fullName
                                                ? `${option.fullName} (${option.userName})`
                                                : option.userName
                                        }
                                        isOptionEqualToValue={(option, value) => option.userRef === value.userRef}
                                        filterOptions={(options, { inputValue }) => {
                                            if (!inputValue) return options.slice(0, 50); // Limit to 50 for performance
                                            return options
                                                .filter(
                                                    (option) =>
                                                        option.userName
                                                            .toLowerCase()
                                                            .includes(inputValue.toLowerCase()) ||
                                                        (option.fullName &&
                                                            option.fullName
                                                                .toLowerCase()
                                                                .includes(inputValue.toLowerCase()))
                                                )
                                                .slice(0, 50);
                                        }}
                                        renderValue={(value) =>
                                            value.map((option: any, index: any) => (
                                                <Chip
                                                    key={index}
                                                    variant="outlined"
                                                    label={
                                                        option.fullName
                                                            ? `${option.fullName} (${option.userName})`
                                                            : option.userName
                                                    }
                                                    onDelete={() => handleRoleManagerDelete(option)}
                                                    className={ER.managersValueChip}
                                                    data-user-ref={option.userRef}
                                                    data-user-name={option.userName}
                                                />
                                            ))
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                className={ER.inputManagersSearch}
                                                label={t('roleManagement.searchUsers')}
                                                placeholder={t('roleManagement.searchUsersPlaceholder')}
                                                variant="outlined"
                                                sx={{ width: 500 }}
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isLoadingUsers ? (
                                                                    <CircularProgress color="inherit" size={20} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    },
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box
                                                component="li"
                                                {...props}
                                                className={`${(props as any).className || ''} ${ER.managersOption}`.trim()}
                                                data-user-ref={option.userRef}
                                                data-user-name={option.userName}
                                            >
                                                <Box>
                                                    <Typography className={ER.managersOptionPrimary} variant="body1">
                                                        {option.fullName || option.userName}
                                                    </Typography>
                                                    <Typography
                                                        className={ER.managersOptionSecondary}
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {option.userName}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        noOptionsText={t('roleManagement.noUsersFound')}
                                    />

                                    {/* Selected Role Managers Display */}
                                    {roleManagers.length > 0 && (
                                        <Box
                                            className={ER.managersSelectedBox}
                                            data-selected-count={roleManagers.length}
                                            sx={{ mt: 2 }}
                                        >
                                            <Typography
                                                className={ER.managersSelectedSummary}
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 1 }}
                                            >
                                                {t('roleManagement.selectedRoleManagers')}: {roleManagers.length}
                                            </Typography>
                                            <Box
                                                className={ER.managersSelectedChipsBox}
                                                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                                            >
                                                {roleManagers.map((user) => (
                                                    <Chip
                                                        key={user.userRef}
                                                        className={ER.managersSelectedChip}
                                                        data-user-ref={user.userRef}
                                                        data-user-name={user.userName}
                                                        label={
                                                            user.fullName
                                                                ? `${user.fullName} (${user.userName})`
                                                                : user.userName
                                                        }
                                                        onDelete={() => handleRoleManagerDelete(user)}
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Grid container justifyContent="flex-end" className={ER.actionsBox}>
                        <SCUIButton
                            className={ER.btnSave}
                            text={'Kaydet'}
                            type="primary"
                            sizeType="fixed"
                            size={'m'}
                            onClick={handleSave}
                        />
                        <Paper></Paper>
                    </Grid>
                </Grid>
            </Grid>
        </SCUIContent>
    );
};
